// unmanaged
//
// Top-level /evaluations page. Two sections:
//   To Do   — runs an Admin flagged (run.evaluate) that the current user
//             hasn't submitted a BioInterpreter entry for yet.
//   History — everything else: envelopes the current user has already
//             evaluated, plus legacy (unflagged) envelopes.
// Columns: Report Owner, Product, Status, Entries, Avg Rating, Latest Entry
// (author + time), Modified.
import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
  Link,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import RateReviewIcon from "@mui/icons-material/RateReview";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import UserAvatar from "./components/UserAvatar";
import { useAuthStore } from "./store/authStore";
import { useEvaluationStore } from "./store/evaluationStore";

const STATUS_LABEL = { open: "Open", in_progress: "In Progress", closed: "Closed" };
const STATUS_COLOR = { open: "info", in_progress: "warning", closed: "success" };

// Flat, non-clipping table shell. DataCard's rounded/translucent Paper sets
// overflow:hidden, which crops this table's right-hand columns whenever it's
// wider than the card (routine now that there are 8 columns) — a plain
// bordered box with no overflow clipping lets TableContainer's own
// overflowX:auto scroll the table instead of silently cutting it off.
const tableHeaderCellSx = {
  color: "#64748B",
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: 0.6,
  borderBottom: "2px solid #E2E8F0",
  backgroundColor: "#F8FAFC",
  py: 1,
  whiteSpace: "nowrap",
};

function fmtWhen(ms) {
  if (!ms) return "—";
  return new Date(Number(ms)).toLocaleString();
}

function StatusCell({ status }) {
  const s = (status || "open").toLowerCase();
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[s] || status || "Open"}
      color={STATUS_COLOR[s] || "default"}
      variant={s === "closed" ? "filled" : "outlined"}
    />
  );
}

function EvaluationRow({ row: r }) {
  // A flagged run now shows one row per known BioInterpreter variant (see
  // the backend's KNOWN_VARIANTS) — r.variant is null for the original/
  // default response, or a name like "20260902" for a named variant. The
  // tab lives at "BioInterpreter" / "BioInterpreter-<variant>" (matches
  // ReportTableBody.jsx's BIOINTERPRETER_VARIANTS label format) — grading
  // and the log both live inside that tab now, no separate Evaluation tab.
  const tab = r.variant ? `BioInterpreter-${r.variant}` : "BioInterpreter";
  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <UserAvatar email={r.run_user_id} size={22} />
          <Typography variant="body2">{r.run_user_id || "—"}</Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{r.run_product || "—"}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Chip size="small" variant="outlined" label={r.variant || "Default"} />
      </TableCell>
      <TableCell sx={{ fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>
        {r.run_user_id && r.report_id ? (
          <Link
            component={RouterLink}
            to={`/report-list/${encodeURIComponent(r.run_user_id)}/${encodeURIComponent(r.report_id)}?tab=${tab}&single=true`}
            underline="hover"
          >
            {r.report_id}
          </Link>
        ) : (
          r.report_id || "—"
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}><StatusCell status={r.status} /></TableCell>
      <TableCell align="right">{r.entry_count ?? 0}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {r.average_rating != null ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Rating value={Number(r.average_rating)} max={5} readOnly precision={0.1} size="small" />
            <Typography variant="caption" sx={{ color: "#64748B" }}>
              {Number(r.average_rating).toFixed(1)}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="caption" sx={{ color: "#94A3B8" }}>—</Typography>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {r.latest_entry_by ? <UserAvatar email={r.latest_entry_by} size={22} /> : "—"}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", color: "#64748B" }}>
        {fmtWhen(r.modified)}
      </TableCell>
    </TableRow>
  );
}

function EvaluationTable({ rows, emptyText }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  return (
    <>
      <TableContainer sx={{ overflowX: "auto", px: 2, boxSizing: "border-box", width: "100%" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderCellSx}>Report Owner</TableCell>
              <TableCell sx={tableHeaderCellSx}>Product</TableCell>
              <TableCell sx={tableHeaderCellSx}>Variant</TableCell>
              <TableCell sx={tableHeaderCellSx}>Report</TableCell>
              <TableCell sx={tableHeaderCellSx}>Status</TableCell>
              <TableCell sx={tableHeaderCellSx} align="right">Entries</TableCell>
              <TableCell sx={tableHeaderCellSx}>Avg Rating</TableCell>
              <TableCell sx={tableHeaderCellSx}>Latest Entry</TableCell>
              <TableCell sx={tableHeaderCellSx}>Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              paged.map((r) => <EvaluationRow key={r.id} row={r} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </>
  );
}

export default function Evaluations() {
  const user = useAuthStore((state) => state.user);
  const userRoles = Array.isArray(user?.roles)
    ? user.roles
    : typeof user?.roles === "string" && user.roles ? [user.roles] : [];
  const isAllowed = userRoles.includes("Admin") || userRoles.includes("InternalUser");

  const fetchAll = useEvaluationStore((s) => s.fetchAll);
  const rows = useEvaluationStore((s) => s.all);
  const isLoading = useEvaluationStore((s) => s.allLoading);
  const error = useEvaluationStore((s) => s.allError);

  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (isAllowed) fetchAll(false);
  }, [isAllowed, fetchAll]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const bag = [
        r.run_user_id, r.run_product, r.run_description, r.report_id, r.status,
        r.latest_entry_by, r.created_by, r.modified_by,
      ].filter(Boolean).join(" ").toLowerCase();
      return bag.includes(q);
    });
  }, [rows, filter]);

  // To Do: Admin-flagged runs this user hasn't submitted a BioInterpreter
  // entry for yet. History: everything else (already done by this user,
  // or a legacy/unflagged envelope).
  const todoRows = useMemo(
    () => filtered.filter((r) => r.run_evaluate && !r.completed_by_me),
    [filtered],
  );
  const historyRows = useMemo(
    () => filtered.filter((r) => !(r.run_evaluate && !r.completed_by_me)),
    [filtered],
  );

  if (!isAllowed) {
    return (
      <PageShell>
        <PageContainer>
          <Alert severity="error" sx={{ mt: 2 }}>
            Evaluations is limited to Admin and InternalUser roles.
          </Alert>
        </PageContainer>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageContainer>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageHeader
            icon={<RateReviewIcon />}
            title="Evaluations"
            subtitle="Runs flagged for evaluation, and your evaluation history."
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <IconButton size="small" onClick={() => fetchAll(true)} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Box>

        {isLoading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ backgroundColor: "#fff", borderRadius: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, p: 2, pb: 1 }}>
              Pending — {todoRows.length}
            </Typography>
            <EvaluationTable rows={todoRows} emptyText="Nothing to evaluate — you're caught up." />

            <Divider sx={{ mt: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 600, p: 2, pb: 1 }}>
              History
            </Typography>
            <EvaluationTable rows={historyRows} emptyText="No evaluations match." />
          </Box>
        )}
      </PageContainer>
    </PageShell>
  );
}
